import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Upload, RefreshCw, Loader2, CheckCircle, Clock,
  FileSpreadsheet, PoundSterling, AlertCircle, Send, Building2,
  Wrench, Shield, FileCheck, Receipt, Link2, ExternalLink
} from "lucide-react";
import { AllHoursIcon } from "@/components/AllHoursLogo";
import { API } from "@/App";

// Xero Account Codes mapping
const XERO_ACCOUNT_CODES = {
  management_fee: { code: "200", name: "Management Fees" },
  letting_fee: { code: "202", name: "Letting Fee" },
  inventories: { code: "204", name: "Inventories" },
  rent_guarantee: { code: "206", name: "Rent Guarantee Insurance" },
  gas_safety: { code: "208", name: "Gas & Elec Safety Checks" },
  eicr: { code: "208", name: "Gas & Elec Safety Checks" },
  epc: { code: "208", name: "Gas & Elec Safety Checks" },
  repairs_markup: { code: "209", name: "Repairs and Maintenance" },
  boiler_service: { code: "210", name: "Other" },
  other: { code: "210", name: "Other" }
};

export const XeroSyncPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [xeroConnected, setXeroConnected] = useState(false);
  const [tenantName, setTenantName] = useState("");
  const [activeTab, setActiveTab] = useState("pending");
  
  // Pending items to sync
  const [pendingStatements, setPendingStatements] = useState([]);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [pendingServices, setPendingServices] = useState([]);
  
  // Selected items for sync
  const [selectedStatements, setSelectedStatements] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  
  // Sync history
  const [syncHistory, setSyncHistory] = useState([]);
  
  // Confirm dialog
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [syncSummary, setSyncSummary] = useState(null);

  useEffect(() => {
    // Handle OAuth callback params
    const connected = searchParams.get("connected");
    const error = searchParams.get("error");
    const tenant = searchParams.get("tenant");
    
    if (connected === "true") {
      toast.success(`Successfully connected to ${tenant || "Xero"}!`);
      // Clear the URL params
      setSearchParams({});
    } else if (error) {
      toast.error(`Xero connection failed: ${error}`);
      setSearchParams({});
    }
    
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [
        xeroStatusRes,
        statementsRes,
        jobsRes,
        servicesRes,
        historyRes
      ] = await Promise.all([
        axios.get(`${API}/xero/status`, { headers }).catch(() => ({ data: { connected: false } })),
        axios.get(`${API}/rent/statements?synced=false&limit=50`, { headers }).catch(() => ({ data: { statements: [] } })),
        axios.get(`${API}/jobs?status=completed&has_invoice=true&synced=false`, { headers }).catch(() => ({ data: { jobs: [] } })),
        axios.get(`${API}/services/pending-sync`, { headers }).catch(() => ({ data: { services: [] } })),
        axios.get(`${API}/xero/sync-history?limit=20`, { headers }).catch(() => ({ data: { history: [] } }))
      ]);

      setXeroConnected(xeroStatusRes.data.connected);
      setTenantName(xeroStatusRes.data.tenant_name || "");
      setPendingStatements(statementsRes.data.statements || []);
      setPendingInvoices(jobsRes.data.jobs || []);
      setPendingServices(servicesRes.data.services || []);
      setSyncHistory(historyRes.data.history || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectXero = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/xero/auth`, { headers });
      if (response.data.auth_url) {
        window.location.href = response.data.auth_url;
      } else {
        toast.error("Failed to get Xero authorization URL");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to connect to Xero");
    }
  };

  const calculateSyncSummary = () => {
    const items = [];
    let totalAmount = 0;

    // Add selected statements (management fees)
    selectedStatements.forEach(id => {
      const stmt = pendingStatements.find(s => s.statement_id === id);
      if (stmt) {
        items.push({
          type: "Management Fee",
          description: `${stmt.landlord_name} - ${stmt.period}`,
          amount: stmt.management_fee || 0,
          vat: (stmt.management_fee || 0) * 0.2,
          xeroAccount: XERO_ACCOUNT_CODES.management_fee.code
        });
        totalAmount += (stmt.management_fee || 0) * 1.2;
      }
    });

    // Add selected contractor invoices (10% markup)
    selectedInvoices.forEach(id => {
      const job = pendingInvoices.find(j => j.job_id === id);
      if (job && job.contractor_invoice_amount) {
        const markup = job.contractor_invoice_amount * 0.1;
        items.push({
          type: "Repairs Markup (10%)",
          description: `${job.property_address} - ${job.description}`,
          amount: markup,
          vat: markup * 0.2,
          xeroAccount: XERO_ACCOUNT_CODES.repairs_markup.code
        });
        totalAmount += markup * 1.2;
      }
    });

    // Add selected services
    selectedServices.forEach(id => {
      const svc = pendingServices.find(s => s.service_id === id);
      if (svc) {
        const accountCode = XERO_ACCOUNT_CODES[svc.service_type] || XERO_ACCOUNT_CODES.other;
        items.push({
          type: svc.service_name,
          description: `${svc.property_address}`,
          amount: svc.amount,
          vat: svc.amount * 0.2,
          xeroAccount: accountCode.code
        });
        totalAmount += svc.amount * 1.2;
      }
    });

    return { items, totalAmount };
  };

  const handlePrepareSync = () => {
    const summary = calculateSyncSummary();
    if (summary.items.length === 0) {
      toast.error("Please select items to sync");
      return;
    }
    setSyncSummary(summary);
    setShowConfirmDialog(true);
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const payload = {
        statements: selectedStatements,
        invoices: selectedInvoices,
        services: selectedServices
      };

      const response = await axios.post(`${API}/xero/sync`, payload, { headers });
      
      toast.success(`Successfully synced ${response.data.synced_count} items to Xero`);
      setShowConfirmDialog(false);
      
      // Clear selections and refresh
      setSelectedStatements([]);
      setSelectedInvoices([]);
      setSelectedServices([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync to Xero");
    } finally {
      setSyncing(false);
    }
  };

  const toggleStatement = (id) => {
    setSelectedStatements(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleInvoice = (id) => {
    setSelectedInvoices(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleService = (id) => {
    setSelectedServices(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectAllStatements = () => {
    if (selectedStatements.length === pendingStatements.length) {
      setSelectedStatements([]);
    } else {
      setSelectedStatements(pendingStatements.map(s => s.statement_id));
    }
  };

  const totalSelected = selectedStatements.length + selectedInvoices.length + selectedServices.length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
          <p className="text-muted-foreground">Loading sync data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-14 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate("/accounts")}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Xero Sync</h1>
                <p className="text-xs text-muted-foreground">Push invoices to Xero accounting</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {xeroConnected ? (
                <Badge className="bg-green-100 text-green-700">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  {tenantName || "Connected to Xero"}
                </Badge>
              ) : (
                <Button onClick={handleConnectXero} className="bg-[#13B5EA] hover:bg-[#0d9fd1]">
                  <Link2 className="w-4 h-4 mr-2" />
                  Connect to Xero
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={fetchData}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!xeroConnected ? (
          /* Not Connected State */
          <Card className="max-w-lg mx-auto mt-12">
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                <FileSpreadsheet className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Connect Your Xero Account</h3>
              <p className="text-muted-foreground mb-6">
                Link your Xero account to sync management fees, contractor invoices, and service charges.
              </p>
              <Button onClick={handleConnectXero} size="lg" className="bg-[#13B5EA] hover:bg-[#0d9fd1]">
                <Link2 className="w-4 h-4 mr-2" />
                Connect to Xero
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Selection Bar */}
            {totalSelected > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge className="bg-blue-600 text-white text-lg px-3 py-1">
                    {totalSelected}
                  </Badge>
                  <span className="font-medium text-blue-800">items selected for sync</span>
                </div>
                <Button onClick={handlePrepareSync} className="bg-green-600 hover:bg-green-700">
                  <Send className="w-4 h-4 mr-2" />
                  Sync to Xero
                </Button>
              </div>
            )}

            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
              <TabsList>
                <TabsTrigger value="pending">
                  <Clock className="w-4 h-4 mr-2" />
                  Pending Sync
                </TabsTrigger>
                <TabsTrigger value="history">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Sync History
                </TabsTrigger>
              </TabsList>

              {/* Pending Tab */}
              <TabsContent value="pending" className="space-y-6">
                {/* Management Fees (from Statements) */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Receipt className="w-5 h-5 text-emerald-600" />
                        Management Fees
                        <Badge variant="secondary">{pendingStatements.length}</Badge>
                      </CardTitle>
                      {pendingStatements.length > 0 && (
                        <Button variant="outline" size="sm" onClick={selectAllStatements}>
                          {selectedStatements.length === pendingStatements.length ? "Deselect All" : "Select All"}
                        </Button>
                      )}
                    </div>
                    <CardDescription>From landlord statements - Account 200</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {pendingStatements.length === 0 ? (
                      <p className="text-muted-foreground text-center py-4">No pending management fees</p>
                    ) : (
                      <div className="space-y-2">
                        {pendingStatements.map(stmt => (
                          <div 
                            key={stmt.statement_id}
                            className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                              selectedStatements.includes(stmt.statement_id) 
                                ? 'bg-blue-50 border-blue-300' 
                                : 'hover:bg-slate-50'
                            }`}
                            onClick={() => toggleStatement(stmt.statement_id)}
                          >
                            <div className="flex items-center gap-3">
                              <Checkbox 
                                checked={selectedStatements.includes(stmt.statement_id)}
                                onCheckedChange={() => toggleStatement(stmt.statement_id)}
                              />
                              <div>
                                <p className="font-medium">{stmt.landlord_name}</p>
                                <p className="text-sm text-muted-foreground">
                                  {stmt.property_address} • {stmt.period}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-emerald-600">
                                £{(stmt.management_fee || 0).toFixed(2)}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                +£{((stmt.management_fee || 0) * 0.2).toFixed(2)} VAT
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Contractor Markup */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Wrench className="w-5 h-5 text-orange-600" />
                      Repairs & Maintenance (10% Markup)
                      <Badge variant="secondary">{pendingInvoices.length}</Badge>
                    </CardTitle>
                    <CardDescription>From completed jobs with contractor invoices - Account 209</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {pendingInvoices.length === 0 ? (
                      <p className="text-muted-foreground text-center py-4">No pending contractor markups</p>
                    ) : (
                      <div className="space-y-2">
                        {pendingInvoices.map(job => (
                          <div 
                            key={job.job_id}
                            className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                              selectedInvoices.includes(job.job_id) 
                                ? 'bg-blue-50 border-blue-300' 
                                : 'hover:bg-slate-50'
                            }`}
                            onClick={() => toggleInvoice(job.job_id)}
                          >
                            <div className="flex items-center gap-3">
                              <Checkbox 
                                checked={selectedInvoices.includes(job.job_id)}
                                onCheckedChange={() => toggleInvoice(job.job_id)}
                              />
                              <div>
                                <p className="font-medium">{job.description}</p>
                                <p className="text-sm text-muted-foreground">
                                  {job.property_address} • Contractor: £{job.contractor_invoice_amount?.toFixed(2)}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-orange-600">
                                £{((job.contractor_invoice_amount || 0) * 0.1).toFixed(2)}
                              </p>
                              <p className="text-xs text-muted-foreground">10% markup</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Services (Gas, EICR, EPC, Boiler, Inventories) */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Shield className="w-5 h-5 text-blue-600" />
                      Services & Compliance Fees
                      <Badge variant="secondary">{pendingServices.length}</Badge>
                    </CardTitle>
                    <CardDescription>Gas safety, EICR, EPC, boiler service, inventories - Various accounts</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {pendingServices.length === 0 ? (
                      <p className="text-muted-foreground text-center py-4">No pending service fees</p>
                    ) : (
                      <div className="space-y-2">
                        {pendingServices.map(svc => (
                          <div 
                            key={svc.service_id}
                            className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                              selectedServices.includes(svc.service_id) 
                                ? 'bg-blue-50 border-blue-300' 
                                : 'hover:bg-slate-50'
                            }`}
                            onClick={() => toggleService(svc.service_id)}
                          >
                            <div className="flex items-center gap-3">
                              <Checkbox 
                                checked={selectedServices.includes(svc.service_id)}
                                onCheckedChange={() => toggleService(svc.service_id)}
                              />
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium">{svc.service_name}</p>
                                  <Badge variant="outline" className="text-xs">
                                    {XERO_ACCOUNT_CODES[svc.service_type]?.code || "210"}
                                  </Badge>
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  {svc.property_address} • {svc.landlord_name}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-blue-600">
                                £{(svc.amount || 0).toFixed(2)}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                +£{((svc.amount || 0) * 0.2).toFixed(2)} VAT
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* History Tab */}
              <TabsContent value="history">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Sync History</CardTitle>
                    <CardDescription>Recently synced items to Xero</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {syncHistory.length === 0 ? (
                      <p className="text-muted-foreground text-center py-8">No sync history yet</p>
                    ) : (
                      <div className="space-y-3">
                        {syncHistory.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              </div>
                              <div>
                                <p className="font-medium">{item.description}</p>
                                <p className="text-sm text-muted-foreground">
                                  {item.type} • Xero Invoice #{item.xero_invoice_number}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-medium">£{item.amount?.toFixed(2)}</p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(item.synced_at).toLocaleDateString('en-GB')}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}
      </main>

      {/* Confirm Sync Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-blue-600" />
              Confirm Xero Sync
            </DialogTitle>
            <DialogDescription>
              Review items before syncing to Xero. Invoices will be created as DRAFT.
            </DialogDescription>
          </DialogHeader>
          
          {syncSummary && (
            <div className="space-y-4 py-4 max-h-[400px] overflow-y-auto">
              {syncSummary.items.map((item, idx) => (
                <div key={idx} className="flex justify-between items-start p-3 bg-slate-50 rounded-lg">
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{item.xeroAccount}</Badge>
                      <span className="font-medium text-sm">{item.type}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{item.description}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">£{item.amount.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">+£{item.vat.toFixed(2)} VAT</p>
                  </div>
                </div>
              ))}
              
              <div className="border-t pt-4 mt-4">
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Total (inc. VAT)</span>
                  <span className="text-xl font-bold text-emerald-600">
                    £{syncSummary.totalAmount.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSync} 
              disabled={syncing}
              className="bg-[#13B5EA] hover:bg-[#0d9fd1]"
            >
              {syncing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Sync to Xero
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default XeroSyncPage;
